import { mergeWeatherSeries } from "./fieldAppWeather";

function minIso(a, b) {
  if (!a) return b || null;
  if (!b) return a || null;
  return a <= b ? a : b;
}

function maxIso(a, b) {
  if (!a) return b || null;
  if (!b) return a || null;
  return a >= b ? a : b;
}

function mergeMachines(existing, incoming) {
  const byKey = {};

  [...(existing || []), ...(incoming || [])].forEach((machine) => {
    const key = machine.ident || String(machine.id || "");
    if (!key) return;

    if (!byKey[key]) {
      byKey[key] = {
        ...machine,
        gpsIntervals: [...(machine.gpsIntervals || [])],
      };
      return;
    }

    const prev = byKey[key];
    prev.pointCount = (prev.pointCount || 0) + (machine.pointCount || 0);
    prev.firstPoint = minIso(prev.firstPoint, machine.firstPoint);
    prev.lastPoint = maxIso(prev.lastPoint, machine.lastPoint);
    prev.gpsIntervals = [...(prev.gpsIntervals || []), ...(machine.gpsIntervals || [])];
  });

  return Object.values(byKey);
}

function mergeSummary(a, b) {
  if (!a) return b || null;
  if (!b) return a || null;

  const pointCount = (a.pointCount || 0) + (b.pointCount || 0);
  const distanceKm = Number(a.distanceKm || 0) + Number(b.distanceKm || 0);
  const movingTimeHours =
    Number(a.movingTimeHours || 0) + Number(b.movingTimeHours || 0);
  const avgSpeedKmh =
    pointCount > 0
      ? ((Number(a.avgSpeedKmh || 0) * (a.pointCount || 0) +
          Number(b.avgSpeedKmh || 0) * (b.pointCount || 0)) /
          pointCount)
      : 0;

  return {
    pointCount,
    distanceKm: Math.round(distanceKm * 100) / 100,
    avgSpeedKmh: Math.round(avgSpeedKmh * 10) / 10,
    maxSpeedKmh: Math.max(Number(a.maxSpeedKmh || 0), Number(b.maxSpeedKmh || 0)),
    movingTimeHours: Math.round(movingTimeHours * 100) / 100,
    firstPoint: minIso(a.firstPoint, b.firstPoint),
    lastPoint: maxIso(a.lastPoint, b.lastPoint),
  };
}

function mergeFuelSummary(a, b) {
  if (!a) return b || null;
  if (!b) return a || null;

  const records = [...(a.records || []), ...(b.records || [])].sort((left, right) =>
    String(left.timestamp).localeCompare(String(right.timestamp))
  );
  const totalLiters = Number(a.totalLiters || 0) + Number(b.totalLiters || 0);
  const recordCount = records.length;

  const byMachine = {};
  [...(a.byMachine || []), ...(b.byMachine || [])].forEach((item) => {
    const ident = item.ident || "—";
    const bucket = byMachine[ident] || { ident, liters: 0, count: 0 };
    bucket.liters += Number(item.liters || 0);
    bucket.count += Number(item.count || 0);
    byMachine[ident] = bucket;
  });

  const machines = Object.values(byMachine)
    .map((item) => ({
      ident: item.ident,
      liters: Math.round(item.liters * 100) / 100,
      count: item.count,
      avgLiters: item.count ? Math.round((item.liters / item.count) * 100) / 100 : 0,
    }))
    .sort((left, right) => left.ident.localeCompare(right.ident));

  return {
    totalLiters: Math.round(totalLiters * 100) / 100,
    recordCount,
    avgLitersPerStop: recordCount
      ? Math.round((totalLiters / recordCount) * 100) / 100
      : 0,
    byMachine: machines,
    records,
  };
}

export function hasDisplayableTracking(tracking) {
  if (!tracking) return false;
  return Boolean(
    (tracking.map && tracking.map.lines && tracking.map.lines.length) ||
      (tracking.operatorGraphLines && tracking.operatorGraphLines.length) ||
      tracking.operatorPointCount ||
      tracking.machinePointCount
  );
}

export function mergeTrackingPayloads(partials) {
  if (!partials || !partials.length) return null;
  if (partials.length === 1) return partials[0];

  return partials.reduce((accumulator, next) => {
    if (!accumulator) return next;
    if (!next) return accumulator;

    const mapA = accumulator.map || {};
    const mapB = next.map || {};

    return {
      operators: accumulator.operators || next.operators || [],
      machines: mergeMachines(accumulator.machines, next.machines),
      operatorPointCount:
        (accumulator.operatorPointCount || 0) + (next.operatorPointCount || 0),
      machinePointCount:
        (accumulator.machinePointCount || 0) + (next.machinePointCount || 0),
      positionRange: {
        init: minIso(
          accumulator.positionRange && accumulator.positionRange.init,
          next.positionRange && next.positionRange.init
        ),
        end: maxIso(
          accumulator.positionRange && accumulator.positionRange.end,
          next.positionRange && next.positionRange.end
        ),
      },
      summary: mergeSummary(accumulator.summary, next.summary),
      map: {
        ...mapA,
        centroidLat:
          mapA.centroidLat != null ? mapA.centroidLat : mapB.centroidLat,
        centroidLng:
          mapA.centroidLng != null ? mapA.centroidLng : mapB.centroidLng,
        lines: [...(mapA.lines || []), ...(mapB.lines || [])],
        stopped_points: [
          ...(mapA.stopped_points || []),
          ...(mapB.stopped_points || []),
        ],
        geojson: mapA.geojson || mapB.geojson || "",
        operation_type: mapA.operation_type || mapB.operation_type || "field_app",
      },
      operatorGraphLines: [
        ...(accumulator.operatorGraphLines || []),
        ...(next.operatorGraphLines || []),
      ],
      equipment: accumulator.equipment || next.equipment || null,
      plannedSpeedKmh: accumulator.plannedSpeedKmh || next.plannedSpeedKmh,
      activityLanes: [
        ...(accumulator.activityLanes || []),
        ...(next.activityLanes || []),
      ],
      fuelSummary: mergeFuelSummary(accumulator.fuelSummary, next.fuelSummary),
      weather: mergeWeatherSeries(accumulator.weather, next.weather),
    };
  }, null);
}
